/**
 * France-specific route type (SNCF product code) resolution for transport badges.
 *
 * SNCF GTFS trips encode a Navitia commercial-mode code inside their trip id
 * (e.g. `sncf_OCESN9764F1187_F:LYR:FR:Line::…` → `LYR`). This module resolves
 * that code to a product icon and label so UI surfaces can render SNCF product
 * badges instead of internal route names (`622E`, `K1`, …).
 *
 * A strict whitelist is applied: only codes observed in the SNCF feed resolve
 * to an icon. Anything else falls back to `null` so callers keep their
 * existing textual badge rendering.
 */

import IceIcon from "../image/icons/route_types/SNCF/ICE.svg";
import SncfIcon from "../image/icons/route_types/SNCF/SNCF.svg";
import TerIcon from "../image/icons/route_types/SNCF/TER.svg";
import TgvInouiIcon from "../image/icons/route_types/SNCF/TGV_inOui.svg";
import TgvLyriaIcon from "../image/icons/route_types/SNCF/TGV_Lyria.svg";
import TgvOuigoIcon from "../image/icons/route_types/SNCF/TGV_Ouigo.svg";
import type { GtfsRoute } from "../types/Gtfs.types";

/** France-specific route type codes mapped to display labels. */
export const ROUTE_TYPE_FRANCE_LABEL_BY_CODE: Readonly<Record<string, string>> = {
    "TER": "TER",
    "CTE": "TER",
    "OGO": "TGV OUIGO",
    "OUI": "TGV INOUI",
    "LYR": "TGV Lyria",
    "ICE": "TGV ICE",
    "IC":  "Intercité",
    "ICN": "Intercité",
};

/** France-specific route type codes that must not appear on the island subline. */
export const ROUTE_TYPE_FRANCE_HIDDEN_CODES: ReadonlySet<string> = new Set( [ "TRN", "TT" ] );

/**
 * Dedicated product icon per route type code.
 *
 * `CTE` (TER coach services) intentionally shares the TER icon: it is the same
 * commercial product family even though the vehicle mode may be a coach.
 */
const ROUTE_TYPE_FRANCE_ICON_BY_CODE: Readonly<Record<string, string>> = {
    "TER": TerIcon,
    "CTE": TerIcon,
    "OGO": TgvOuigoIcon,
    "OUI": TgvInouiIcon,
    "LYR": TgvLyriaIcon,
    "ICE": IceIcon,
};

/**
 * Known SNCF product codes without a dedicated asset.
 *
 * These codes are observed in the SNCF feed but have no product icon in
 * `src/image/icons/route_types/SNCF/`; they render the generic SNCF logo.
 * Codes absent from this list and from {@link ROUTE_TYPE_FRANCE_ICON_BY_CODE}
 * never resolve to an icon (whitelist behavior protects against garbage codes).
 */
const ROUTE_TYPE_FRANCE_GENERIC_ICON_CODES: ReadonlySet<string> =
    new Set( [ "IC", "ICN", "CRE", "NAV", "TRN", "TT", "NA" ] );

/**
 * Captures the Navitia commercial-mode segment following the `OCE…` train id token.
 *
 * Matches both raw static ids (`sncf_OCESN9764F1187_F:LYR:FR:…`) and
 * MOTIS-decorated ids (`20260918_08:21_sncf_OCESN9764F1187_F:LYR:FR:…`).
 * Stop ids such as `StopPoint:OCECar TER-87746008` cannot match because the
 * `OCE…` token is not followed by a `:CODE:` pair.
 */
const SNCF_OCE_TRIP_ID_PATTERN = /OCE[^:]*:([A-Za-z0-9]+):/;

/**
 * Public line names of the Léman Express, jointly operated by SNCF and CFF.
 * These trips are coded `TER` in the SNCF trip id, but the rider-facing name is L1–L7.
 */
const LEMAN_EXPRESS_LINE_NAME_PATTERN = /^L[1-7]$/;

/** France product codes that render the TER logo. */
const TER_PRODUCT_CODES: ReadonlySet<string> = new Set( [ "TER", "CTE" ] );

/** Resolved France product badge: icon asset URL and accessible label. */
export interface RouteTypeFranceBadge {

    /** Imported SVG asset URL for the product icon. */
    "icon": string;

    /** Human-readable product label for `alt` / `title` attributes. */
    "label": string;
}

/**
 * Normalizes a raw France route type code for lookups.
 *
 * @param code Raw `route_type_france` / extracted code value.
 * @returns Uppercased trimmed code, or `null` when empty or absent.
 */
export const normalizeRouteTypeFranceCode = ( code: string | null | undefined ): string | null => {
    if ( code === null || code === undefined ) {
        return null;
    }

    const normalizedCode = code.trim().toUpperCase();

    return normalizedCode === "" ? null : normalizedCode;
};

/**
 * Maps a France-specific route type code to its island subline label.
 *
 * @param code Raw `route_type_france` wire value.
 * @returns Display label, or `null` when the code must not be shown.
 */
export const mapRouteTypeFranceLabel = ( code: string | null | undefined ): string | null => {
    const normalizedCode = normalizeRouteTypeFranceCode( code );

    if ( normalizedCode === null || ROUTE_TYPE_FRANCE_HIDDEN_CODES.has( normalizedCode ) ) {
        return null;
    }

    return ROUTE_TYPE_FRANCE_LABEL_BY_CODE[normalizedCode] ?? null;
};

/**
 * Resolves a France route type code to a product badge icon and label.
 *
 * Codes with a dedicated asset return their product icon; known SNCF codes
 * without a dedicated asset return the generic SNCF logo; every other value
 * returns `null` so callers keep their textual badge fallback.
 *
 * @param code Raw or normalized France route type code.
 * @returns Badge icon + label, or `null` when no SNCF icon applies.
 */
export const resolveRouteTypeFranceBadge = ( code: string | null | undefined ): RouteTypeFranceBadge | null => {
    const normalizedCode = normalizeRouteTypeFranceCode( code );

    if ( normalizedCode === null ) {
        return null;
    }

    const dedicatedIcon = ROUTE_TYPE_FRANCE_ICON_BY_CODE[normalizedCode];

    if ( dedicatedIcon !== undefined ) {
        return {
            "icon":  dedicatedIcon,
            "label": ROUTE_TYPE_FRANCE_LABEL_BY_CODE[normalizedCode] ?? normalizedCode,
        };
    }

    if ( ROUTE_TYPE_FRANCE_GENERIC_ICON_CODES.has( normalizedCode ) ) {
        return {
            "icon":  SncfIcon,
            "label": ROUTE_TYPE_FRANCE_LABEL_BY_CODE[normalizedCode] ?? normalizedCode,
        };
    }

    return null;
};

/**
 * Whether a public line name is a Léman Express line (L1–L7).
 *
 * @param publicLineName Route short name or vehicle label shown to the rider.
 * @returns `true` when the trimmed name is exactly `L1` … `L7`.
 */
export const isLemanExpressLineName = ( publicLineName: string | null | undefined ): boolean => {
    if ( publicLineName === null || publicLineName === undefined ) {
        return false;
    }

    return LEMAN_EXPRESS_LINE_NAME_PATTERN.test( publicLineName.trim().toUpperCase() );
};

/**
 * Resolves an SNCF product badge, keeping Léman Express line names visible.
 *
 * L1–L7 are coded `TER` in the SNCF feed. Showing the TER logo there hides the
 * public line name, so those lines return `null` and callers render `L1`, `L2`, …
 *
 * @param code Raw or normalized France route type code.
 * @param publicLineName Route short name or vehicle label shown beside the badge.
 * @returns Badge icon + label, or `null` when the textual line name must stay.
 */
export const resolveRouteTypeFranceBadgeForLine = (
    code: string | null | undefined,
    publicLineName: string | null | undefined,
): RouteTypeFranceBadge | null => {
    const normalizedCode = normalizeRouteTypeFranceCode( code );

    if (
        normalizedCode !== null &&
        TER_PRODUCT_CODES.has( normalizedCode ) &&
        isLemanExpressLineName( publicLineName )
    ) {
        return null;
    }

    return resolveRouteTypeFranceBadge( code );
};

/**
 * Extracts the France route type code from a GTFS or MOTIS trip identifier.
 *
 * @param tripId Static (`sncf_OCE…:CODE:…`) or MOTIS-decorated (`date_time_sncf_OCE…:CODE:…`) trip id.
 * @returns Normalized product code, or `null` when the id is not an SNCF Navitia id.
 */
export const extractRouteTypeFranceCodeFromTripId = ( tripId: string | null | undefined ): string | null => {
    if ( tripId === null || tripId === undefined ) {
        return null;
    }

    const match = SNCF_OCE_TRIP_ID_PATTERN.exec( tripId );

    return match === null ? null : normalizeRouteTypeFranceCode( match[1] );
};

/**
 * Extracts the commercial train number from a Navitia/OCE trip id (e.g. `OCESN96510…` → `96510`).
 *
 * Mirrors `extract_commercial_train_number` in `RealtimeTransports/src/gtfs_rt/trip_id_normalize.rs`:
 * locate the `OCE` token, drop an optional `SN` marker, then take the first digit run.
 *
 * @param tripId Static (`sncf_OCE…:CODE:…`) or MOTIS-decorated (`date_time_sncf_OCE…:CODE:…`) trip id.
 * @returns Digit-only train number, or `null` when `OCE` is missing or no digits follow.
 */
export const extractCommercialTrainNumberFromTripId = ( tripId: string | null | undefined ): string | null => {
    if ( tripId === null || tripId === undefined ) {
        return null;
    }

    const oceIndex = tripId.indexOf( "OCE" );

    if ( oceIndex === -1 ) {
        return null;
    }

    let rest = tripId.slice( oceIndex + 3 );

    if ( rest.startsWith( "SN" ) ) {
        rest = rest.slice( 2 );
    }

    const digitsMatch = ( /\d+/ ).exec( rest );

    return digitsMatch === null ? null : digitsMatch[0];
};

/**
 * Removes duplicate France product badges from a route list.
 *
 * Two routes resolving to the same icon are visually identical badges, so only
 * the first occurrence is kept. Routes without a resolved badge (non-SNCF or
 * unknown codes) are never dropped and keep their original order.
 *
 * @param routes Correspondence routes as returned by stop-lines lookup.
 * @returns Deduplicated route list preserving the incoming order.
 */
export const dedupeRoutesByFranceBadge = ( routes: readonly GtfsRoute[] ): GtfsRoute[] => {
    const seenIconUrls = new Set<string>();

    return routes.filter( ( route ) => {
        const badge = resolveRouteTypeFranceBadgeForLine( route.routeTypeFrance, route.name );

        if ( badge === null ) {
            return true;
        }

        if ( seenIconUrls.has( badge.icon ) ) {
            return false;
        }

        seenIconUrls.add( badge.icon );

        return true;
    } );
};
